import { Button, Card, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MessageInstance } from 'antd/es/message/interface';
import { useMemo } from 'react';

import { apiPost } from '../../api.js';
import { registryApiBase, writeLocalBaseline } from '../utils.js';
import type { BaselineSnapshot, HistoryItem } from '../types.js';

type HistoryCardProps = {
  registryUrl: string;
  history: HistoryItem[];
  baseline: BaselineSnapshot | null;
  onBaselineChange: (baseline: BaselineSnapshot | null) => void;
  messageApi: MessageInstance;
};

export function HistoryCard({
  registryUrl,
  history,
  baseline,
  onBaselineChange,
  messageApi,
}: HistoryCardProps) {
  const columns: ColumnsType<HistoryItem> = useMemo(
    () => [
      {
        title: 'When',
        dataIndex: 'ts',
        key: 'ts',
        width: 220,
        render: (value: string) => new Date(value).toLocaleString(),
      },
      {
        title: 'URL',
        dataIndex: 'url',
        key: 'url',
        render: (value: string) => (
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        ),
      },
      { title: 'new 3P', dataIndex: 'new3p', key: 'new3p', width: 90 },
      {
        title: 'Actions',
        key: 'actions',
        width: 260,
        render: (_, row) => (
          <Space>
            <Button
              size="small"
              onClick={async () => {
                try {
                  if (!row.id) return;
                  const detailUrl = `${registryApiBase(registryUrl)}/v1/scans/${encodeURIComponent(row.id)}`;
                  const r = await fetch(detailUrl, { cache: 'no-store' });
                  if (!r.ok) throw new Error('Failed to load scan detail');
                  const detail = (await r.json()) as {
                    id: string;
                    gpc?: boolean;
                    analysis?: { headers?: { issues?: string[] }; cookies?: { issues?: string[] } };
                    diff?: { newThirdPartyHosts?: string[] };
                    finalUrl?: string;
                    url?: string;
                  };
                  const baselineData: BaselineSnapshot = {
                    id: detail.id,
                    headerIssues: detail.analysis?.headers?.issues?.length || 0,
                    cookieIssues: detail.analysis?.cookies?.issues?.length || 0,
                    new3p: detail.diff?.newThirdPartyHosts?.length || 0,
                    new3pHosts: detail.diff?.newThirdPartyHosts || [],
                  };
                  const resolvedUrl = detail.finalUrl || detail.url || row.url;
                  writeLocalBaseline(resolvedUrl, Boolean(detail.gpc), baselineData);
                  onBaselineChange(baselineData);
                  try {
                    await apiPost(
                      `${registryApiBase(registryUrl)}/admin/scans/${encodeURIComponent(detail.id)}/baseline`,
                    );
                    messageApi.success('Promoted to baseline (server)');
                  } catch {
                    messageApi.success('Promoted to baseline (local)');
                  }
                } catch (err) {
                  messageApi.error((err as Error)?.message || 'Failed to promote baseline');
                }
              }}
            >
              Promote baseline
            </Button>
          </Space>
        ),
      },
    ],
    [messageApi, onBaselineChange, registryUrl],
  );

  return (
    <Card title="Recent history">
      {history.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <strong>new3P trend</strong>
          <div
            style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 40, marginTop: 4 }}
          >
            {(() => {
              const values = history.map((item) => item.new3p);
              const max = Math.max(1, ...values);
              return values.map((value, index) => {
                const height = Math.round((value / max) * 36) + 2;
                return (
                  <div
                    key={index}
                    title={`${new Date(history[index].ts).toLocaleString()}: ${value}`}
                    style={{ width: 8, height, background: '#1677ff', borderRadius: 2 }}
                  />
                );
              });
            })()}
          </div>
        </div>
      )}
      <Table<HistoryItem>
        dataSource={history}
        rowKey={(row) => (row.id || row.url) + row.ts}
        columns={columns}
        size="small"
        pagination={false}
      />
      {baseline && (
        <div style={{ marginTop: 8, color: '#666' }}>
          Baseline — headers: <Tag>{baseline.headerIssues}</Tag> cookies:{' '}
          <Tag>{baseline.cookieIssues}</Tag> new 3P: <Tag>{baseline.new3p}</Tag>
        </div>
      )}
    </Card>
  );
}
